import type React from "react";
import type {ComponentProps} from "react";

import {AppAlertDialog} from "@project/components/overlays";
import {Button, Typography} from "@project/design-system-web";
import {createContext, useCallback, useContext, useState} from "react";

interface ShellConfirmOptions {
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  status?: ComponentProps<typeof AppAlertDialog.Icon>["status"];
  confirmVariant?: ComponentProps<typeof Button>["variant"];
}

interface ShellConfirmState {
  options: ShellConfirmOptions;
  resolve: (confirmed: boolean) => void;
}

type ShellConfirm = (options: ShellConfirmOptions) => Promise<boolean>;

const ShellConfirmContext = createContext<ShellConfirm | null>(null);

interface ShellConfirmProviderProps {
  children: React.ReactNode;
}

export function ShellConfirmProvider({children}: ShellConfirmProviderProps) {
  const [state, setState] = useState<ShellConfirmState | null>(null);

  const confirm = useCallback<ShellConfirm>((options) => {
    return new Promise<boolean>((resolve) => {
      setState({options, resolve});
    });
  }, []);

  const closeWith = useCallback((confirmed: boolean) => {
    setState((current) => {
      current?.resolve(confirmed);

      return null;
    });
  }, []);

  return (
    <ShellConfirmContext.Provider value={confirm}>
      {children}

      <AppAlertDialog.Root
        isOpen={!!state}
        onOpenChange={(open) => {
          if (!open) closeWith(false);
        }}
      >
        <AppAlertDialog.Backdrop>
          <AppAlertDialog.Container>
            <AppAlertDialog.Dialog className="sm:max-w-100">
              <AppAlertDialog.CloseTrigger />
              <AppAlertDialog.Header>
                <AppAlertDialog.Icon status={state?.options.status ?? "warning"} />
                <AppAlertDialog.Heading>{state?.options.title}</AppAlertDialog.Heading>
              </AppAlertDialog.Header>
              <AppAlertDialog.Body>
                {typeof state?.options.description === "string" ? (
                  <Typography>{state.options.description}</Typography>
                ) : (
                  state?.options.description
                )}
              </AppAlertDialog.Body>
              <AppAlertDialog.Footer>
                <Button variant="tertiary" onPress={() => closeWith(false)}>
                  {state?.options.cancelLabel ?? "Cancel"}
                </Button>
                <Button
                  variant={state?.options.confirmVariant ?? "primary"}
                  onPress={() => closeWith(true)}
                >
                  {state?.options.confirmLabel ?? "Confirm"}
                </Button>
              </AppAlertDialog.Footer>
            </AppAlertDialog.Dialog>
          </AppAlertDialog.Container>
        </AppAlertDialog.Backdrop>
      </AppAlertDialog.Root>
    </ShellConfirmContext.Provider>
  );
}

export function useShellConfirm() {
  const confirm = useContext(ShellConfirmContext);

  if (!confirm) {
    throw new Error("useShellConfirm must be used within ShellConfirmProvider");
  }

  return confirm;
}
